'use client';

import { Copytext } from '@/app/_components/core/copytext';
import { useLocale } from '@/app/_contexts/locale-context';
import {
  formatEventDateRange as formatDateRange,
  formatDate,
  formatTime,
} from '@/app/_lib/date-utils';
import { cn, emptyToUndefined } from '@/app/_lib/utils';

export interface CardDateLocationProps {
  startDateTime?: string | null;
  endDateTime?: string | null;
  location?: string;
  filterLocation?: string;
  /** Render date as a large, bold heading with date-only format (used in grid variant). */
  prominent?: boolean;
  /** Render the date with title-like font styling instead of the default/prominent date styling. */
  asTitle?: boolean;
}

export function CardDateLocation({
  startDateTime,
  endDateTime,
  location,
  filterLocation,
  prominent = false,
  asTitle = false,
}: CardDateLocationProps) {
  const locale = useLocale();
  const isFutureEvent =
    !!startDateTime && new Date(startDateTime) > new Date();
  const displayDate = prominent
    ? startDateTime
      ? formatDate(startDateTime, locale)
      : null
    : startDateTime
      ? formatDateRange(startDateTime, emptyToUndefined(endDateTime), locale) || null
      : null;

  // emptyToUndefined so an empty-string filterLocation still falls back to the
  // full location — TYPO3 returns '' instead of null when no filter override is set.
  const displayLocation = emptyToUndefined(filterLocation) ?? location;
  // Prefix the start time only when the heading shows date-only (prominent).
  // In the default layout the heading already includes the date/time range,
  // so adding the time to the location would duplicate it.
  const locationWithTime =
    prominent && isFutureEvent && startDateTime && displayLocation
      ? `${formatTime(startDateTime, locale)}, ${displayLocation}`
      : displayLocation;

  return (
    <>
      {displayDate && (
        <Copytext
          as="time"
          dateTime={startDateTime ?? undefined}
          className={cn(
            'mb-2 block min-w-0 truncate',
            prominent &&
              !asTitle &&
              'text-card-foreground lg:text-2xl lg:font-extrabold',
            asTitle &&
              'my-2 text-lg/normal  font-medium text-card-foreground'
          )}
        >
          {displayDate}
        </Copytext>
      )}
      {locationWithTime && (
        <Copytext variant="muted" className="mb-2 font-normal">
          {locationWithTime}
        </Copytext>
      )}
    </>
  );
}